import { MB } from "../common/constants";
import { provinceHasPostalCode } from "../common/utils/countryProvince";
import { compareFoundryVersions } from "../common/utils/versionComparison";
import { Enums } from "../common/enums";
import slugify from "../common/utils/slugify";
import UI from "./ui";
import Assets from "./assets";
import utilsDependencies from "./utils-dependencies";
import { INDEXEDDB_VERSION_DEFAULT } from "./constants";

export default class Utils {
    // From https://hackernoon.com/copying-text-to-clipboard-with-javascript-df4d4988697f
    static copyToClipboard(str) {
        const el = document.createElement("textarea"); // Create a <textarea> element
        el.value = str; // Set its value to the string that you want copied
        el.setAttribute("readonly", ""); // Make it readonly to be tamper-proof
        el.style.position = "absolute";
        el.style.left = "-9999px"; // Move outside the screen to make it invisible
        document.body.appendChild(el); // Append the <textarea> element to the HTML document
        const selected =
            document.getSelection().rangeCount > 0 // Check if there is any content selected previously
                ? document.getSelection().getRangeAt(0) // Store selection if found
                : false; // Mark as false to know no selection existed before
        el.select(); // Select the <textarea> content
        document.execCommand("copy"); // Copy - only works as a result of a user action (e.g. click events)
        document.body.removeChild(el); // Remove the <textarea> element
        if (selected) {
            // If a selection existed before copying
            document.getSelection().removeAllRanges(); // Unselect everything on the HTML document
            document.getSelection().addRange(selected); // Restore the original selection
        }
    }

    /* Hash a string and return its hex digest */
    static async hash(data) {
        const encoder = new TextEncoder();
        const buffer = encoder.encode(data);
        const hash = await crypto.subtle.digest("SHA-256", buffer);
        return this.ArrayBufferToHex(hash);
    }
    static ArrayBufferToHex(ab) {
        return Array.from(new Int8Array(ab), function (byte) {
            return (byte & 0xff).toString(16).padStart(2, "0");
        }).join("");
    }
    static md5sum(data) {
        const spark = new SparkMD5();
        spark.append(data);
        return spark.end();
    }
    static async etagFromFile(file, progress = null) {
        return new Promise((resolve, reject) => {
            const blobSlice = File.prototype.slice || File.prototype.mozSlice || File.prototype.webkitSlice;
            const chunkSize = 5 * MB; // Read in chunks of 5MB
            const chunks = Math.ceil(file.size / chunkSize);
            let currentChunk = 0;
            const spark = new SparkMD5.ArrayBuffer();
            const fileReader = new FileReader();
            let sparkMulti = null;

            if (progress) {
                progress(0);
            }
            fileReader.onload = function (e) {
                spark.append(e.target.result); // Append array buffer
                currentChunk++;

                if (progress) {
                    // If cancelled, end hashing.
                    if (!progress(currentChunk / chunks)) {
                        // Need both resolve and return, otherwise we could call loadNext() below and continue hashing
                        resolve(false);
                        return false;
                    }
                }
                if (currentChunk < chunks) {
                    if (!sparkMulti) {
                        sparkMulti = new SparkMD5();
                    }
                    sparkMulti.appendBinary(spark.end(true));
                    spark.reset();
                    loadNext();
                } else {
                    if (sparkMulti) {
                        sparkMulti.appendBinary(spark.end(true));
                        resolve(`${sparkMulti.end()}-${chunks}`);
                    } else {
                        resolve(spark.end());
                    }
                }
            };

            fileReader.onerror = function (err) {
                reject(err);
            };

            function loadNext() {
                var start = currentChunk * chunkSize,
                    end = start + chunkSize >= file.size ? file.size : start + chunkSize;

                fileReader.readAsArrayBuffer(blobSlice.call(file, start, end));
            }

            loadNext();
        });
    }
    static fileext(path) {
        return path.split(".").pop();
    }
    static basename(path) {
        return path
            .replace(/\\/g, "/")
            .replace(/\/+$|^\/+/g, "")
            .split("/")
            .reverse()[0];
    }
    static dirname(path) {
        return path
            .replace(/\\/g, "/")
            .replace(/\/+$|^\/+/g, "")
            .split("/")
            .slice(0, -1)
            .join("/");
    }
    static cleanPath(path) {
        return path.replace(/\/+$|^\/+/g, "").replace(/\/+/g, "/");
    }
    static prefixPath(path, filename, prefix) {
        return prefix === "" || path === prefix || filename === `${prefix}/` || path.startsWith(`${prefix}/`);
    }
    static bytesToHuman(bytes) {
        // t('{{count}} Bytes', { count })
        // t('{{count}} KB', { count })
        // t('{{count}} MB', { count })
        // t('{{count}} GB', { count })
        // t('{{count}} TB', { count })
        const sizes = ["{{count}} Bytes", "{{count}} KB", "{{count}} MB", "{{count}} GB", "{{count}} TB"];
        if (bytes == 0) {
            return t("{{count}} Bytes", { count: 0 });
        }
        const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
        const decimals = i === 0 ? 0 : 2; // Don't show a fraction of a byte
        return t(sizes[i], { count: (bytes / Math.pow(1024, i)).toFixed(decimals) });
    }

    static async blobToString(blob) {
        return new Promise((resolve) => {
            const reader = new FileReader();

            reader.onerror = () => resolve(null);
            reader.onload = () => resolve(reader.result);
            reader.readAsText(blob);
        });
    }
    static async blobToJson(blob) {
        const string = await this.blobToString(blob);
        try {
            return JSON.parse(string);
        } catch (err) {
            return null;
        }
    }

    static async blobToBase64(blob) {
        // Return `null` instead of promise rejections in case of errors
        return Utils.getDataURL(blob)
            .then((result) => result)
            .catch(() => null);
    }

    static duplicate(obj) {
        return JSON.parse(JSON.stringify(obj));
    }
    static objectsAreEqual(obj1, obj2) {
        return JSON.stringify(obj1) === JSON.stringify(obj2);
    }

    static slugify = slugify;

    /**
     * Generate a localized string representing the timespan between now and another date.
     * @param {Date} date           The reference date (either Date or ISO String). This is compared to `Date.now()` to determine the timespan.
     * @param {string} granularity  The granularity of the timespan. "d" for days, "h" for hours, "m" for minutes, "s" for seconds.
     * @param {boolean} timespan    Whether to return a timespan string (e.g. "2 days ago") or a time string (e.g. "2 days").
     * @param {number} fullDateCutoff  The number of days after which to show the full date instead of a timespan.
     * @param {string} fullDateCutoffLabel  The label to show instead of the full date when the full date cutoff is reached.
     * @returns {string}            The localized timespan string.
     */
    static timeSince(
        date,
        granularity = "s",
        timespan = true,
        fullDateCutoff = Number.MAX_SAFE_INTEGER,
        fullDateCutoffLabel = ""
    ) {
        if (!date) {
            return t("Unknown");
        }
        if (typeof date === "string" || date instanceof String) {
            date = new Date(date);
        }
        const elapsed = date - Date.now();
        const seconds = Math.round(elapsed / 1000);
        const minutes = Math.round(seconds / 60);
        const hours = Math.round(minutes / 60);
        const days = Math.round(hours / 24);

        if (Math.abs(days) > fullDateCutoff) {
            if (fullDateCutoffLabel) {
                return fullDateCutoffLabel;
            }
            return date.toLocaleDateString();
        }
        if (Math.abs(days) > 1) {
            return timespan ? t("TIMESPAN.days", { val: days }) : t("TIME.days", { count: days });
        }
        if (granularity === "d") {
            if (days === 0) {
                return t("Today");
            }
            if (days === 1) {
                return t("Tomorrow");
            }
            if (days === -1) {
                return t("Yesterday");
            }
        }
        // Hours
        if (Math.abs(hours) > 1) {
            return timespan ? t("TIMESPAN.hours", { val: hours }) : t("TIME.hours", { count: hours });
        }
        if (granularity === "h") {
            return t("Now");
        }
        // Minutes
        if (Math.abs(minutes) > 1) {
            return timespan ? t("TIMESPAN.minutes", { val: minutes }) : t("TIME.minutes", { count: minutes });
        }
        if (granularity === "m") {
            return t("Now");
        }
        // Seconds
        if (Math.abs(seconds) > 1) {
            return timespan ? t("TIMESPAN.seconds", { val: seconds }) : t("TIME.seconds", { count: seconds });
        }
        return t("Now");
    }

    static duration(durationInSeconds) {
        const seconds = Math.round(durationInSeconds);
        const minutes = Math.round(seconds / 60);
        const hours = Math.round(minutes / 60);
        const days = Math.round(hours / 24);
        if (days) {
            return t("TIME.days", { count: days });
        }
        if (hours) {
            return t("TIME.hours", { count: hours });
        }
        if (minutes) {
            return t("TIME.minutes", { count: minutes });
        }
        return t("TIME.seconds", { count: seconds });
    }

    static getCookies() {
        return Object.fromEntries(
            document.cookie.split(/; */).map((c) => {
                const [key, ...v] = c.split("=");
                return [key, decodeURIComponent(v.join("="))];
            })
        );
    }

    static sortGames(games) {
        return games.sort((a, b) => {
            if (a.favorite && !b.favorite) {
                return -1;
            }
            if (!a.favorite && b.favorite) {
                return 1;
            }
            if (a.owner && !b.owner) {
                return -1;
            }
            if (!a.owner && b.owner) {
                return 1;
            }
            return b.lastAccess - a.lastAccess;
        });
    }

    static capitalize(value) {
        if (!value) {
            return "";
        }
        value = value.toString();
        return value.charAt(0).toUpperCase() + value.slice(1);
    }

    /**
     * Bound a number between some minimum and maximum value, inclusively.
     * @memberof Math
     * @param {number} num    The current value
     * @param {number} min    The minimum allowed value
     * @param {number} max    The maximum allowed value
     * @return {number}       The clamped number
     */
    static clamp(num, min, max) {
        return Math.min(max, Math.max(num, min));
    }

    static isNumeric(n) {
        return isFinite(parseFloat(n));
    }

    /**
     * Restricts any given text input forms to positive integers only (empty or 1+)
     * @param {Object} event  `InputEvent` object, forwarded from the `input.native`
     */
    static validatePositiveIntegerInput(event) {
        const t = event.target;
        if (event.inputType.startsWith("insert") && !/^[1-9]+\d*$/.test(t.value)) {
            // Revert the insert
            const lva = t.dataset.lastValidAmount || "";
            const pos = t.selectionStart - (t.value.length - lva.length);
            t.value = lva;
            t.setSelectionRange(pos, pos);
            return;
        }
        t.dataset.lastValidAmount = t.value;
    }

    static apiKeyToInfo(key) {
        if (!key) {
            return {};
        }
        try {
            return JSON.parse(atob(key.split(".")[1]));
        } catch (err) {
            return {};
        }
    }

    static _apiKeyToHash(key) {
        if (!key) {
            return "";
        }
        return key.split(".")[2] || "";
    }

    static _isAPIKeyExpired(key) {
        if (!key) {
            return true;
        }
        const info = this.apiKeyToInfo(key);
        // token exp field is in epoch seconds, Date.now() is in milliseconds
        // Expire it 1 minute in advance to avoid a race where by the time the request
        // is received on the server, the key has already expired.
        return info.exp && info.exp - 60 < Date.now() / 1000;
    }

    static isValidAPIKey(apiKey) {
        const info = this.apiKeyToInfo(apiKey);
        if (!info.id) {
            return false;
        }
        return !this._isAPIKeyExpired(apiKey);
    }

    /**
     * A wrapper around the browser-native Fetch API
     * @param {string} endpoint - route to fetch
     * @param {RequestInit} options - The fetch call's options.
     */
    static async fetch(endpoint, options = { body: "", method: "GET" }) {
        return fetch(endpoint, options);
    }

    /**
     * Send an API request
     * @param {String} endpoint               API endpoint
     * @param {FormData} formData             Form Data to send. POST if set, GET otherwise
     * @param {Object} options                Options
     * @param {String} options.method         Override API request method to use
     * @param {Function} options.progress     Progress report. function(step, percent)
     *                                        Step 0: Request started
     *                                        Step 1: Uploading request
     *                                        Step 2: Downloading response
     *                                        Step 3: Request completed
     * @param {boolean} [options.json=true]   Send request as JSON, defaults to true
     */
    static async api(endpoint, formData = null, { method, progress, json = true, apiKey } = {}) {
        return new Promise((resolve) => {
            const xhr = new XMLHttpRequest();
            xhr.withCredentials = true;
            //req.setRequestHeader("Authorization", "jwt " + );
            method = method || (formData ? "POST" : "GET");
            const url = endpoint.startsWith("https://") ? endpoint : `/api/${endpoint}`;
            xhr.open(method, url);
            if (apiKey) {
                xhr.setRequestHeader("Access-Key", apiKey);
            }
            const cookies = this.getCookies();
            if (cookies["XSRF-TOKEN"]) {
                xhr.setRequestHeader("X-XSRF-TOKEN", cookies["XSRF-TOKEN"]);
            }
            xhr.responseType = "text";
            if (progress) {
                xhr.onloadstart = () => progress(0, 0);
                xhr.upload.onprogress = (event) => progress(1, event.loaded / event.total);
                xhr.onprogress = (event) => progress(2, event.loaded / event.total);
            }
            xhr.onreadystatechange = () => {
                if (xhr.readyState !== 4) {
                    return;
                }
                if (progress) {
                    progress(3, 1);
                }
                let response = {
                    code: xhr.status,
                    error: xhr.statusText || t(`Error {{status}}`, { status: xhr.status }),
                };
                try {
                    response = JSON.parse(xhr.response);
                } catch (err) {
                    response.message = xhr.responseText;
                }
                resolve(response);
            };
            xhr.onerror = (err) => {
                resolve({ code: 500, error: err.message });
            };
            if (json) {
                xhr.setRequestHeader("Content-type", "application/json; charset=utf-8");
                formData = JSON.stringify(formData);
            }
            xhr.send(formData);
        });
    }

    /**
     * `POST`s an API request, manages most aspects of the task management for the client side,
     * and triggers events based on server's `response.status`
     * @param {String}   endpoint         API endpoint
     * @param {FormData} formData         Form Data to send
     * @param {Object}   options          Options
     * @param {String}   options.method   Override API request method to use
     * @param {Function} options.progress Progress report. function(step, percent)
     *                                    Step 0: Request started
     *                                    Step 1: Uploading request
     *                                    Step 2: Downloading response
     *                                    Step 3: Request completed
     * @param {String}   options.statusRefresh   Timeout to refresh the status of a task (default: 5000)
     * @param {Object}   eventHandlers    `eventName`: `fn(response)`
     * @param {Boolean}  sendInitial      Duplicate and send the first event received (containing
     *                                    one-time only information) with an `initial` status
     * @returns A task object with emitter and promise chainable functions
     */
    static async apiTask(endpoint, formData = {}, options = {}, eventHandlers = {}, sendInitial = false) {
        formData = formData || {};
        options = options || {};
        if (options.json === false) {
            formData.set("async", "true");
        } else {
            formData.async = true; // Support long task operations
        }

        // Emits `response.status` & "status" events, together with an "initial" event
        // that allows processing of the response plus any additional data returned by
        // the task manager together with the first response
        const emit = async (data, initial = false) => {
            // [initial], data.status, 'status' — filter duplicates and false-y statuses
            const events = [data.status, "status"];
            if (initial && data.status !== "initial") {
                events.unshift("initial");
            }
            for (const state of events) {
                if (eventHandlers[state]) {
                    // Detach the callback from further events, if required by the callback itself
                    const removeHandler = await eventHandlers[state](data.status, data);
                    if (removeHandler) {
                        delete eventHandlers[state];
                    }
                }
            }
        };

        let response = await this.api(endpoint, formData, options);
        const task = response.task;

        // Sync/error detection
        if (!task || response.error) {
            return response;
        }

        // Always trigger the `task/status` endpoint to remove completed tasks from the DB
        let initial = sendInitial;
        // This while loop needs to run indefinitely until the task is done
        // eslint-disable-next-line no-constant-condition
        while (true) {
            // Avoid spurious response and unnecessary wait if the promise fullfilled fast
            await emit(response, initial);
            initial = false;
            if (response.status === "done") {
                return response.result;
            } else if (response.error) {
                return response;
            }

            await new Promise((r) => setTimeout(r, options.statusRefresh || 5000));
            response = await this.api(`task/status/${task}`);
        }
    }

    /**
     * Calls api endpoint and requests OTP in case a re-auth of OTP is required
     */
    static async apiAdmin(endpoint, formData, options = {}) {
        let executor;
        let isAsync = formData && formData.async;
        if (options.json === false) {
            isAsync = !!formData.get("async");
        }
        if (isAsync) {
            executor = () => this.apiTask(endpoint, formData, options);
        } else {
            executor = () => this.api(endpoint, formData, options);
        }
        const response = await executor();
        if (response.code === 403 && response.otp) {
            const element = document.createElement("admin2fa-prompt");
            document.body.after(element);
            const vm = await UI.renderVue(null, element);
            const otpResponse = await new Promise((resolve) => {
                vm.$children[0].$on("done", (val) => {
                    resolve(val);
                });
            });
            vm.$destroy();
            if (otpResponse) {
                if (otpResponse.success) {
                    // We confirmed 2FA, retry API
                    return executor();
                } else if (otpResponse.errorCode === "auth.invalid") {
                    window.location = "/account/login";
                }
                return otpResponse;
            }
        }
        return response;
    }

    static camelCaseToKebab(string) {
        return string
            .replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, "$1-$2")
            .toLowerCase()
            .replace(/(^-)|(-$)/, "");
    }

    static escapeHTML(text) {
        return text
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&apos;");
    }

    static extensionType(ext) {
        ext = ext.toLowerCase();
        if (Assets.IMAGE_EXTENSIONS.includes(ext)) {
            return "image";
        }
        if (Assets.VIDEO_EXTENSIONS.includes(ext)) {
            return "video";
        }
        if (Assets.AUDIO_EXTENSIONS.includes(ext)) {
            return "audio";
        }
        if (Assets.ASSETS_3D_EXTENSIONS.includes(ext)) {
            return "3d";
        }
        if (Assets.OTHER_MEDIA_EXTENSIONS.includes(ext)) {
            return ext === "pdf" ? "pdf" : "data";
        }
        return "data";
    }

    static async loadScript(url) {
        return new Promise((resolve, reject) => {
            const head = document.getElementsByTagName("head")[0];
            const script = document.createElement("script");
            script.onload = resolve;
            script.onerror = reject;
            script.src = url;
            head.appendChild(script);
        });
    }

    static async loadCss(config) {
        const head = document.getElementsByTagName("head")[0];
        Utils.loadedCss = Utils.loadedCss || new Set();

        if (config.content) {
            const hash = await this.hash(config.content);
            if (Utils.loadedCss.has(hash)) {
                return;
            }
            Utils.loadedCss.add(hash);
            const style = document.createElement("style");
            style.type = "text/css";

            if (style.styleSheet) {
                style.styleSheet.cssText = config.content;
            } else {
                style.innerHTML = config.content;
            }

            head.appendChild(style);
        } else if (config.url) {
            if (Utils.loadedCss.has(config.url)) {
                return;
            }
            Utils.loadedCss.add(config.url);
            const link = document.createElement("link");
            link.href = config.url;
            link.rel = "stylesheet";
            link.type = "text/css";
            head.appendChild(link);
        }
    }

    static async loadDependencies(dependencies) {
        for (let dep of dependencies) {
            if (!utilsDependencies[dep]) {
                console.warn(`Could not load unknown dependency '${dep}'`);
                continue;
            }
            // To avoid race condition, save the promise and alway await it
            if (!this.LOADED_DEPENDENCIES[dep]) {
                this.LOADED_DEPENDENCIES[dep] = this._loadDependency(dep);
            }
            await this.LOADED_DEPENDENCIES[dep];
        }
    }
    static async _loadDependency(dep) {
        let { scripts, styles, dependencies, init } = utilsDependencies[dep];
        // Recursively load dependencies. Cycles are NOT supported!!
        if (dependencies) {
            if (typeof dependencies === "string") {
                dependencies = [dependencies];
            }
            await this.loadDependencies(dependencies);
        }
        // Load scripts
        if (scripts) {
            if (typeof scripts === "string") {
                scripts = [scripts];
            }
            for (let script of scripts) {
                await this.loadScript(script);
            }
        }
        // Load styles
        if (styles) {
            if (typeof styles === "string") {
                styles = [styles];
            }
            for (let style of styles) {
                await this.loadCss({ url: style });
            }
        }
        // Init the dependency if needed
        if (init) {
            await init();
        }
        console.log("Loaded dependency ", dep);
    }

    static downloadAsCSV(array, { filename = "data.csv", transform = {} } = {}) {
        const escapeCSV = (v, t = String) => `"${t(v || "").replace(/"/g, '""')}"`;
        // Get a merged list of all the keys from every element in the array
        const keys = array
            .map((a) => Object.keys(a))
            .reduce((keys, rowKeys) => {
                rowKeys.forEach((k) => {
                    if (!keys.includes(k)) {
                        keys.push(k);
                    }
                });
                return keys;
            }, []);
        const csv =
            `${keys.map((k) => escapeCSV(k)).join(",")}\n` +
            array
                .map((row) => {
                    // ensure the same order for the keys
                    return keys.map((k) => escapeCSV(row[k], transform[k] || String)).join(",");
                })
                .join("\n");
        const csvContent = "data:text/csv;charset=utf-8;base64," + btoa(csv.replace(/[\u0250-\ue007]/g, "")); // Remove non latin characters so btoa works
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", filename);
        document.body.appendChild(link); // Required for FF

        link.click(); // This will download the data file

        // Remove the link after 5 seconds
        setTimeout(() => document.body.removeChild(link), 5000);
    }

    static async getDataURL(blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });
    }
    static hashProps(hash) {
        return hash
            .slice(1)
            .split("&")
            .reduce((props, prop) => {
                const [key, value] = prop.split("=");
                if (key) {
                    props[decodeURIComponent(key)] = decodeURIComponent(value || "");
                }
                return props;
            }, {});
    }

    static buildHash(hashProps, overrides = {}) {
        const properties = Object.assign({}, hashProps, overrides);
        return Object.entries(properties)
            .filter(([k, v]) => k && v)
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join("&");
    }

    static errorLoadingImage(ev, defaultImage = "/images/file-unknown.png", errorText) {
        // If image fails to load, then fallback to default image
        const img = ev.target;
        if (!img) {
            return;
        }
        if (img.attributes.src.value === defaultImage) {
            return;
        }
        img.src = defaultImage;
        if (errorText) {
            img.title = errorText;
        }
    }

    /**
     * Checks if the given parameter is an Object
     * @param {*} o
     */
    static isObject(o) {
        return !Array.isArray(o) && Object(o) === o;
    }

    /**
     * Auto hide flash messages with timeout attribute after timeout
     */
    static timeoutFlashMessages() {
        $(".flash-message").each(function () {
            const msg = $(this);
            const timeout = parseInt(msg.attr("timeout")) || 0;
            if (timeout > 0) {
                setTimeout(() => msg.hide(), timeout);
            }
        });
    }

    /**
     * Get a link to the forums that prompts sign-in if user signed out
     * @param {string} path Optional, path on forums to link to. For example, '/t/{topicId}' for a specific topic
     * @returns {string}    Forum link
     */
    static getForumLink(path = "") {
        return `https://forums.forge-vtt.com/session/sso?return_path=https://forums.forge-vtt.com${path}`;
    }

    /**
     * Localizes the date using the standard browser functionality
     * @param {*}        date   Date to be parsed
     * @param {Boolean=} short  Short form
     * @returns
     */
    static localizeDate(date, short = false) {
        const d = new Date(date);
        return short
            ? d.toLocaleString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
              })
            : d.toLocaleString(undefined, { dateStyle: "full", timeStyle: "medium" });
    }

    /**
     * Compares two Foundry VTT versions. This doesn't do semantic versioning comparison, but is limited
     * to Foundry's own version format.
     *
     * Foundry has two formats for version numbers specifically for compatibility checks:
     * - 'Generation': A single digit that indicates 'entire generation', such as '11' = all v11 versions
     * - 'Build': A 'specific build', such as 11.308, where 308 is the build number.
     *
     * When specifically doing a Foundry compatibility check, you can pass 'allowGenerationalEquivalence' as true.
     *
     * In that case, if either 'a' or 'b' is a 'Generation', rather than a 'Build',
     *  treat all 'Builds' of that 'Generation' as equal to the 'Generation' for comparison purposes.
     *
     * This is particularly useful for maximum compatibility checks, where 11 might indicate compatibility with 11.308.
     * @param {String} a
     * @param {String} b
     * @param {Boolean} [allowGenerationalEquivalence=false] Treat 'Generations' as equal to all 'Builds' of that 'Generation'
     * @deprecated in favor of relocating it to /common/utils.
     * @see {@link /common/utils/versionComparison.js}
     */
    static compareFoundryVersions(a, b, allowGenerationalEquivalence = false) {
        return compareFoundryVersions(a, b, allowGenerationalEquivalence);
    }

    static async uploadDataFile(path, data, { overwrite = true, type = "application/octet-stream" } = {}) {
        const formData = new FormData();
        const blob = data instanceof Blob ? data : new Blob([data], { type });
        formData.append("file", blob);
        formData.append("overwrite", overwrite);
        // Can't use a relative endpoint since it's not an /api/ path
        const url = `${window.location.origin}/data/${path}`;
        const result = await Utils.api(url, formData, { method: "PUT", json: false });
        console.log("Write File Result", result);
        return { success: result.code === 200, error: result.error };
    }

    static NullishCoalescing(...args) {
        for (let arg in args) {
            if (arg !== null && arg !== undefined) {
                return arg;
            }
        }
    }

    static getEmbeddedJSON(id) {
        const element = document.getElementById(id);
        if (!element) {
            return null;
        }
        try {
            // Parse the JSON and unescape the </script>
            return JSON.parse(element.innerHTML.replace(/<\\\/script>/g, "</script>"));
        } catch (err) {
            return null;
        }
    }

    /**
     * Converts Foundry jsonlines log to something renderable by Vue
     * @param {String} logs  JSON-lines formatted logs
     * @returns {Array} logs, newest to oldest
     */
    static parseFoundryLogs(logs) {
        return logs.split("\n").reduce((logs, line) => {
            if (line.trim().length === 0) {
                return logs;
            }
            const l = this.parseFoundryLogLine(line);
            if (l) {
                logs.unshift(l);
            }
            return logs;
        }, []);
    }
    static parseFoundryLogLine(line) {
        try {
            const l = JSON.parse(line);
            l.message = l.message.split("\n");
            if (l.stack) {
                l.stack = l.stack.split("\n");
            }
            return l;
        } catch (err) {
            return null;
        }
    }

    /**
     * Specifically for use in Vue. As translate, but sets interpolation.escapeValue to false.
     * This should only be used in cases where the framework already escapes unsafe HTML.
     * @param {string|string[]} source  Translation key or array for multiple fallback keys
     * @param {string} [defaultValue]   Default translation. Can be omitted instead of passing null
     * @param {object} [options]        Extra parameters (depends on i18n provider: https://www.i18next.com/translation-function/essentials)
     * @returns {string}                target || default || source
     */
    static t_vue(...args) {
        return UI.i18n.translateNoEscape(...args);
    }

    /**
     * Returns the rrule mapping for weekdays
     * @param {int} weekDay The weekday as integer as defined in javascript
     * @returns {object} The rrule mapping for the given weekday
     */
    static getRruleWeekdayMapping(weekDay) {
        switch (weekDay) {
            case Enums.DayOfWeek.SUNDAY:
                return window.rrule.RRule.SU;
            case Enums.DayOfWeek.MONDAY:
                return window.rrule.RRule.MO;
            case Enums.DayOfWeek.TUESDAY:
                return window.rrule.RRule.TU;
            case Enums.DayOfWeek.WEDNESDAY:
                return window.rrule.RRule.WE;
            case Enums.DayOfWeek.THURSDAY:
                return window.rrule.RRule.TH;
            case Enums.DayOfWeek.FRIDAY:
                return window.rrule.RRule.FR;
            case Enums.DayOfWeek.SATURDAY:
                return window.rrule.RRule.SA;
            default:
                return window.rrule.RRule.SU;
        }
    }

    /**
     * Return an object containing the date and time fields of a date object, formatted into a specific timezone
     */
    static getDateTimeFields(date, tzid) {
        const formatter = new Intl.DateTimeFormat("en-US", {
            timeZone: tzid,
            timeZoneName: "long",
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
            second: "numeric",
            hour12: false,
        });
        const parts = formatter.formatToParts(date).reduce((obj, part) => {
            obj[part.type] = part.value;
            return obj;
        }, {});
        const timeZoneOffset = parts.timeZoneName.match(/([+-]\d{2}):(\d{2})/);
        return {
            year: parts.year,
            month: parts.month,
            day: parts.day,
            hour: parts.hour,
            minute: parts.minute,
            second: parts.second,
            timeZoneOffset: timeZoneOffset ? timeZoneOffset[0] : "Z",
        };
    }

    /**
     * Generate an ISO date string from the given date fields
     *
     * @param {int} year The year
     * @param {int} month The month
     * @param {int} day The day
     * @param {int} hours The hours
     * @param {int} minutes The minutes
     * @param {int} seconds The seconds
     * @param {string} timeZoneOffset The timezone offset (defaults to UTC)
     * @returns {string} The ISO date string
     * @static
     */
    static generateISODateString({ year, month, day, hour, minute, second }, timeZoneOffset = "Z") {
        const yearStr = year.toString().padStart(4, "0");
        const monthStr = month.toString().padStart(2, "0");
        const dayStr = day.toString().padStart(2, "0");
        // Hour 24 should be written as 00
        const hoursStr = hour.toString() === "24" ? "00" : hour.toString().padStart(2, "0");
        const minutesStr = minute.toString().padStart(2, "0");
        const secondsStr = second.toString().padStart(2, "0");
        return `${yearStr}-${monthStr}-${dayStr}T${hoursStr}:${minutesStr}:${secondsStr}${timeZoneOffset}`;
    }

    /**
     * Convert a date into a Date object in which the UTC fields are actually
     * in the timezone specified in argument
     * This is needed because rrule is moronic and uses UTC fields for datetime values
     * but it expects them to be in the specific timezone, instead of using the Date
     * object like it's meant to be used.
     *
     * @param {Date} date       The date to convert
     * @param {String} tzid     The Timezone ID
     */
    static convertDateFromTimezoneToUTCFields(date, tzid) {
        // Convert the start date from the local timezone to the timezone of the game owner
        // Then format it to turn it into a ISO string under the UTC timezone
        const dateFields = this.getDateTimeFields(date, tzid);
        const utcDate = this.generateISODateString(
            dateFields,
            "Z" // Force UTC timezone
        );
        return new Date(utcDate);
    }

    /**
     * Compute rrule given the recurrence data
     * @param {object} nextSessionData This is the recurrence data containing
     * start date, timezone and repeat frequency
     * @returns {object} The rrule object
     */
    static computeRrule(nextSessionData) {
        let rruleFreq = window.rrule.RRule.WEEKLY;
        let byweekday;
        let count = null;
        const interval = nextSessionData.repeatInterval !== undefined ? nextSessionData.repeatInterval : 1;
        if (nextSessionData.repeats.length === 0) {
            count = 1;
        } else if (nextSessionData.repeats.length > 1) {
            rruleFreq = window.rrule.RRule.DAILY;
            byweekday = nextSessionData.repeats.map((day) => this.getRruleWeekdayMapping(day));
        } else {
            byweekday = nextSessionData.repeats.map((day) => this.getRruleWeekdayMapping(day));
        }
        // Convert the start date from the local timezone to the timezone of the game owner
        // Then format it to turn it into a ISO string under the UTC timezone
        const startDate = new Date(nextSessionData.startDate);
        const utcDate = this.convertDateFromTimezoneToUTCFields(startDate, nextSessionData.tzid);
        const rule = new window.rrule.RRule({
            // HACK ALERT: rrule timezone support is broken, it expects the date to be in UTC time
            // but on the timezone specified by tzid. So we need to create a Date using values in the UTC
            // timezone but containing the date and time values from the tzid timezone
            dtstart: utcDate,
            freq: rruleFreq,
            count,
            interval,
            byweekday,
            tzid: nextSessionData.tzid,
        });
        return rule;
    }

    static calculateNextDateFromRepeats(nextSessionData, afterDate = new Date()) {
        const rule = this.computeRrule(nextSessionData);
        // Because the rrule uses a tzid, we need to provide it with a date for 'after' that is also in that
        // same timezone under the UTC fields.
        const afterUtcDate = this.convertDateFromTimezoneToUTCFields(afterDate, nextSessionData.tzid);
        const date = rule.after(afterUtcDate);
        if (!date) {
            return null;
        }
        // HACK ALERT: We receive a date from rrule in which the values of the UTC time are actually
        // in the local timezone so we need to convert it back to a normal Date object
        return new Date(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate(),
            date.getUTCHours(),
            date.getUTCMinutes(),
            date.getUTCSeconds()
        );
    }

    static generatePassword(blocks) {
        const characters = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ";
        const specialAndNumbers = "23456789@&!$?_#";
        let password = "";
        for (let i = 0; i < blocks; i++) {
            // block is 3 characters and then special character or number, we do not use similar characters
            for (let j = 0; j < 3; j++) {
                password += characters.charAt(Math.floor(Math.random() * characters.length));
            }
            password += specialAndNumbers.charAt(Math.floor(Math.random() * specialAndNumbers.length));
        }
        return password;
    }

    /**
     * Given a string, compress it with gzip compression and return a Blob.
     * with its contents.
     *
     * @param str {String}  The input string.
     *
     * @returns   {Blob}    The compressed blob.
     */
    static async compressString(str) {
        let chunks = [];
        try {
            const encoder = new TextEncoder();
            const data = encoder.encode(str);
            const stream = new Blob([data]).stream().pipeThrough(new CompressionStream("gzip"));
            for await (const chunk of stream) {
                chunks.push(chunk);
            }
        } catch {
            throw new Error("Failed to compress string");
        }
        return new Blob(chunks);
    }

    /**
     * Open an IndexedDB for use elsewhere.
     *
     * @param {string} dbKey  The DB to open.
     * @param {string} tableKey  The table we're planning to use.
     * @param {number} dbVersion  The version of the IndexedDB schema for the DB.
     *
     * @returns {Promise}  A promise that contains the requested DB.
     */
    static openIndexedDB(dbKey, tableKey, dbVersion = INDEXEDDB_VERSION_DEFAULT) {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(dbKey, dbVersion);
            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                if (tableKey) {
                    if (!db.objectStoreNames.contains(tableKey)) {
                        db.createObjectStore(tableKey);
                    }
                }
            };
            request.onsuccess = (event) => {
                resolve(event.target.result);
            };
            request.onerror = (event) => {
                reject(event.target.error);
            };
        });
    }

    /**
     * Given an object, stringify and compress it, then store it in IndexedDB.
     *
     * @param {Object} obj  The input object.
     * @param {string} dbKey  The DB to store the object in.
     * @param {string} tableKey  The table to store the object in.
     * @param {string} storeKey  The row to store the object in.
     * @param {number} schema  The schema version number of the data to be
     * compressed.
     *
     * @returns        {Blob}    The compressed blob.
     */
    static async compressAndStore(obj, dbKey, tableKey, storeKey, schema, dbVersion) {
        let blob = null;
        try {
            blob = await this.compressString(JSON.stringify(obj));
            const db = await this.openIndexedDB(dbKey, tableKey, dbVersion);
            await new Promise((resolve, reject) => {
                const transaction = db.transaction(tableKey, "readwrite");
                const objectStore = transaction.objectStore(tableKey);
                try {
                    const request = objectStore.put(
                        {
                            data: blob,
                            lastUpdate: Date.now(),
                            schema,
                        },
                        storeKey
                    );

                    request.onsuccess = resolve;
                    request.onerror = (event) => reject(event.target.error);
                } catch (error) {
                    reject(error);
                }
            });
        } catch (error) {
            throw new Error(`Failed to compress the provided object: ${error}`);
        }
        return blob;
    }

    /**
     * Given a Blob that contains a string compressed with Utils.compressString,
     * decompress it with gzip and return the original string.
     *
     * @param blob   {Blob}     The input blob.
     * @param asJSON {Boolean}  Should the return value be parsed JSON?
     *
     * @returns      {String}   The originally-compressed string.
     */
    static async decompress(blob, asJSON = false) {
        let str = "";
        try {
            const stream = blob.stream().pipeThrough(new DecompressionStream("gzip"));
            const chunks = [];
            for await (const chunk of stream) {
                chunks.push(chunk);
            }
            const concatenated = new Uint8Array(await new Blob(chunks).arrayBuffer());
            const decoder = new TextDecoder();
            str = decoder.decode(concatenated);
        } catch (e) {
            throw new Error("Failed to decompress the requested blob.");
        }
        if (asJSON) {
            try {
                return JSON.parse(str);
            } catch {
                return str;
            }
        }
        return str;
    }

    /**
     * Given a series of keys that point to a compressed blob in an IndexedDB,
     * return its decompressed contents.
     *
     * @param dbKey    {string}  The DB to look in.
     * @param tableKey {string}  The table to look in.
     * @param storeKey {string}  The row to look in.
     *
     * @returns        {object}  The now-decompressed object.
     */
    static async retrieveAndDecompress(dbKey, tableKey, storeKey, dbVersion) {
        try {
            const db = await this.openIndexedDB(dbKey, tableKey, dbVersion);
            const { data, ...row } = await new Promise((resolve, reject) => {
                const transaction = db.transaction(tableKey, "readonly");
                const objectStore = transaction.objectStore(tableKey);
                const request = objectStore.get(storeKey);
                request.onsuccess = (event) => resolve(event.target.result);
                request.onerror = (event) => reject(event.target.error);
            });

            return {
                packageData: await this.decompress(data, true),
                ...row,
            };
        } catch {
            return null;
        }
    }

    /**
     * Given a series of keys that point to a store in an IndexedDB,
     * delete the target store.
     *
     * @param dbKey    {string}  The DB to look in.
     * @param tableKey {string}  The table to look in.
     * @param storeKey {string}  The row to look in.
     *
     * @returns        {boolean} Did we successfully delete the blob?
     */
    static async clearDBEntry(dbKey, tableKey, storeKey) {
        try {
            const db = await this.openIndexedDB(dbKey, tableKey);
            await new Promise((resolve, reject) => {
                const transaction = db.transaction(tableKey, "readonly");
                const objectStore = transaction.objectStore(tableKey);
                const request = objectStore.delete(storeKey);
                request.onsuccess = () => resolve();
                request.onerror = (event) => reject(event.target.error);
            });
        } catch {
            return false;
        }
        return true;
    }
    /**
     * A set of logging methods.
     */
    static get Logger() {
        const domainStart = (rgb) => `\x1B[38;2;${rgb.join(";")};4m`;
        const defaultDomainColor = domainStart([40, 167, 69]);
        const formatEnd = "\x1B[m";

        const colorize = (message, rgb = null) => {
            const formatStart = rgb && Array.isArray(rgb) ? domainStart(rgb) : defaultDomainColor;
            return `${formatStart}${message.toUpperCase()}${formatEnd}`;
        };

        return {
            /**
             * Given a domain and a message, return a colorized log.
             *
             * @param domain {string} - A name for the relevant part of the app.
             * @param message {any} - The logged message to display.
             * @param options {unknown} - Options to provide to the log output.
             * @param options.domainColor {string} - an array of unsigned 8-bit integers representing an RGB color. Used to override the default Forge Dark Green domain color.
             * @param _context {unknown} - Context for the log that could be passed to some sort of external tool or service, if we ever choose to do so.
             *
             * @example
             * // logs "EXAMPLE: Hello World!" with "EXAMPLE" in dark green
             * Utils.Logger.info("example", "Hello world!");
             *
             * @example
             * // logs "EXAMPLE: Hello World!" with "EXAMPLE" in pure red
             * Utils.Logger.info("example", "Hello world!", { domainColor: [255, 0, 0] });
             *
             * @example
             * // logs "HELLO WORLD!:" in dark green
             * Utils.Logger.info("Hello world!");
             *
             * @example
             * // logs "Hello World!" without color or special formatting
             * Utils.Logger.info(null, "Hello world!");
             */
            info: (domain, message, options = {}, _context = {}) => {
                if (!domain) {
                    console.info(message);
                    return;
                }
                console.info(`${colorize(domain, options ? options.domainColor : null)}:`, message);
            },
        };
    }

    /**
     * Sanitises a given path, removing extraneous slashes and other problematic characters for Windows OS
     * @see https://docs.microsoft.com/en-us/windows/win32/fileio/naming-a-file#naming-conventions
     * @param {*} path The string name to be sanitized
     * @returns {string} String The sanitized value
     */
    static sanitizePath(path) {
        path = path
            .replace(/:+/g, "_58_")
            .replace(/<+/g, "_60_")
            .replace(/>+/g, "_62_")
            .replace(/"+/g, "_34_")
            .replace(/\|+/g, "_124_")
            .replace(/\?+/g, "_63_")
            .replace(/\*+/g, "_42_")
            /* eslint-disable-next-line no-control-regex */
            .replace(/[\u0000-\u001F\u007F\uFFFE\uFFFF\t]/g, "�");

        return path;
    }
}
Utils.provinceHasPostalCode = provinceHasPostalCode;
Utils.LOADED_DEPENDENCIES = {};
